import dvaModelExtend from 'dva-model-extend';
import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate, getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';
import { createCrudModel } from '@/utils/core/dva';

import { ENTITY_NAME, FETCH_ROUTE, MODEL_NAME } from './constants';

const api = bindServices(MODEL_NAME);
const { BulkActionType } = CoreEnums;

const namespace = MODEL_NAME;
const fetchRoute = FETCH_ROUTE;
const fetchParams = 'AiSkillGroup';

// String enum variant required by import endpoints (numeric enum is CRUD-only).
const buildImportBody = (payload) => {
  const provider = Number(payload?.provider);
  if (provider === CoreEnums.SkillSourceProvider.Git) {
    return {
      sourceProvider: 'git',
      sourceUrl: payload?.sourceUrl?.trim?.() || payload?.sourceUrl,
    };
  }
  if (provider === CoreEnums.SkillSourceProvider.Zip) {
    return {
      sourceProvider: 'zip',
      ...(payload?.owner ? { owner: payload.owner } : {}),
      ...(payload?.sourcePath ? { sourcePath: payload.sourcePath } : {}),
      ...(payload?.zipContent ? { zipContent: payload.zipContent } : {}),
    };
  }
  return {};
};

const _model = createCrudModel({
  namespace,
  fetchRoute,
  fetchParams,
  entityName: ENTITY_NAME,
  api,
  features: { import: false, bulkActions: false },
  state: {
    bulkActionErrorData: null,
    showBulkActionErrors: false,
    showImportGroup: false,
    importPreview: null,
    importPreviewRequest: null,
    importResult: null,
    importGroupId: null,
  },
  effects: {
    *bulkDeleteItems({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateData = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
          select: stateData.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateItems({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkItemActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkDeactivateItems({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkItemActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *openImportGroup({ payload }, { put }) {
      yield put({
        type: 'queryOpenImportGroup',
        payload: {
          groupId: payload?.groupId ?? null,
        },
      });
    },

    *closeImportGroup(_, { put }) {
      yield put({ type: 'queryCloseImportGroup' });
    },

    *closeImportPreview(_, { put }) {
      yield put({ type: 'queryClosePreview' });
    },

    *fetchImportGroupPreview({ payload }, { call, put }) {
      try {
        const body = buildImportBody(payload);
        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import/preview`,
          method: 'post',
          data: body,
        });
        yield put({
          type: 'queryImportPreview',
          payload: { preview: res, request: body },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchImportGroup({ payload }, { call, put, select }) {
      try {
        const stateData = yield select((state) => state[namespace]);
        const base = stateData.importPreviewRequest || buildImportBody(payload);
        const body = {
          ...base,
          ...(payload?.name ? { name: payload.name } : {}),
          ...(payload?.description ? { description: payload.description } : {}),
          allowSkillGroupOverride: !!payload?.allowSkillGroupOverride,
          ...(stateData.importGroupId ? { groupId: stateData.importGroupId } : {}),
        };

        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import`,
          method: 'post',
          data: body,
        });

        yield put({
          type: 'queryImportGroup',
          payload: { result: res },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
        };
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *toggleBulkActionErrorsDrawer({ payload }, { put }) {
      yield put({
        type: 'queryToggleBulkActionErrorsDrawer',
        payload,
      });
    },

    *clearBulkActionErrors(_, { put }) {
      yield put({ type: 'queryClearBulkActionErrors' });
    },
  },
  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryOpenImportGroup(state, { payload }) {
      return {
        ...state,
        showImportGroup: true,
        importGroupId: payload?.groupId ?? null,
        importPreview: null,
        importPreviewRequest: null,
        importResult: null,
      };
    },

    queryCloseImportGroup(state) {
      return {
        ...state,
        showImportGroup: false,
        importGroupId: null,
        importPreview: null,
        importPreviewRequest: null,
        importResult: null,
      };
    },

    queryImportPreview(state, { payload }) {
      return {
        ...state,
        importPreview: payload.preview,
        importPreviewRequest: payload.request,
      };
    },

    queryClosePreview(state) {
      return {
        ...state,
        importPreview: null,
        importPreviewRequest: null,
      };
    },

    queryImportGroup(state, { payload }) {
      return {
        ...state,
        showImportGroup: false,
        importGroupId: null,
        importPreview: null,
        importPreviewRequest: null,
        importResult: payload.result,
      };
    },

    setBulkActionErrors(state, { payload }) {
      return {
        ...state,
        bulkActionErrorData: payload.errorData,
        showBulkActionErrors: true,
      };
    },

    queryToggleBulkActionErrorsDrawer(state, { payload }) {
      return { ...state, showBulkActionErrors: payload.open };
    },

    queryClearBulkActionErrors(state) {
      return { ...state, bulkActionErrorData: null, showBulkActionErrors: false };
    },
  },
});

export default dvaModelExtend(_model, { namespace });
